import type { HoSoThue, LoaiThueTncn } from '@qlk/shared';
import { LOAI_THUE_TNCN, LOAI_THUE_TNCN_LABEL, LOAI_THUE_TNCN_MO_TA } from '@qlk/shared';
import { useState } from 'react';

import { HopThoai, Nut, O, OChonTruong } from '../../../ui/index.js';
import { useLuuHoSoThue } from '../api.js';

export interface HopHoSoThueProps {
  userId: string;
  tenNguoi: string;
  /** Hồ sơ đang lưu (đã che số định danh); `null` khi chưa khai. */
  dangCo: HoSoThue | null;
  onDong: () => void;
}

/**
 * Sửa hồ sơ thuế của một nhân sự. Chỉ quản trị mở được hộp này.
 *
 * ⚠️ Ô CCCD / MST / số tài khoản để **trống** chứ không điền sẵn giá trị đã
 * che: giá trị đã che (`••••6789`) mà nằm trong ô nhập thì bấm Lưu là ghi đúng
 * chuỗi dấu chấm đó vào database. Trống = "giữ nguyên"; nhập = "thay bằng".
 */
export function HopHoSoThue({ userId, tenNguoi, dangCo, onDong }: HopHoSoThueProps) {
  const luu = useLuuHoSoThue();

  const [hoTen, setHoTen] = useState(dangCo?.hoTenDayDu ?? '');
  const [cccd, setCccd] = useState('');
  const [mst, setMst] = useState('');
  const [soTk, setSoTk] = useState('');
  const [nganHang, setNganHang] = useState(dangCo?.nganHang ?? '');
  const [loaiThue, setLoaiThue] = useState<LoaiThueTncn>(dangCo?.loaiThue ?? 'khau_tru_10');

  /**
   * Ô trống nghĩa là **giữ nguyên** — nhưng server nhận cả sáu trường, nên phải
   * gửi lại giá trị cũ. Giá trị cũ đã che, nên "giữ nguyên" chỉ làm được khi
   * người dùng **không** sửa ô đó: gửi chuỗi rỗng sẽ xoá mất số đang có.
   *
   * Cách giải: ô trống thì gửi `undefined` lên không được (server đòi đủ), nên
   * giao diện nói rõ — trống là xoá. Muốn giữ thì nhập lại. Thà rõ ràng còn hơn
   * âm thầm ghi `••••6789`.
   */
  const coSoCu = (dangCo?.cccd ?? '') !== '' || (dangCo?.soTaiKhoan ?? '') !== '';

  return (
    <HopThoai
      title={`Hồ sơ thuế — ${tenNguoi}`}
      rong="lg"
      onDong={onDong}
      chanHop={
        <>
          {luu.isError && (
            <p role="alert" className="mr-auto self-center text-phu text-bad-chu">
              {luu.error.message}
            </p>
          )}
          <Nut kieu="phu" onClick={onDong}>
            Huỷ
          </Nut>
          <Nut
            kieu="chinh"
            dangChay={luu.isPending}
            onClick={() => {
              luu.mutate(
                {
                  userId,
                  hoTenDayDu: hoTen.trim(),
                  cccd: cccd.trim(),
                  mstCaNhan: mst.trim(),
                  soTaiKhoan: soTk.trim(),
                  nganHang: nganHang.trim(),
                  loaiThue,
                },
                { onSuccess: onDong },
              );
            }}
          >
            Lưu hồ sơ
          </Nut>
        </>
      }
    >
      <div className="space-y-4">
        <O
          label="Họ và tên đầy đủ"
          value={hoTen}
          onChange={setHoTen}
          moTa="In trên biên bản. Khác tên tài khoản thì lấy tên này."
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <O label="CCCD" value={cccd} onChange={setCccd} />
          <O label="MST cá nhân" value={mst} onChange={setMst} />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <O label="Số tài khoản" value={soTk} onChange={setSoTk} />
          <O label="Ngân hàng" value={nganHang} onChange={setNganHang} />
        </div>

        {coSoCu && (
          <p className="rounded-[10px] bg-warn-nen px-3 py-2 text-phu text-warn-chu">
            Ba ô định danh để <strong>trống</strong> vì giá trị đang lưu đã che bớt — lưu với ô
            trống là <strong>xoá</strong> số đang có. Muốn giữ thì nhập lại đầy đủ.
          </p>
        )}

        <OChonTruong
          label="Diện thuế TNCN"
          value={loaiThue}
          onChange={(v) => {
            setLoaiThue(v as LoaiThueTncn);
          }}
          options={LOAI_THUE_TNCN.map((l) => ({ value: l, label: LOAI_THUE_TNCN_LABEL[l] }))}
          moTa={LOAI_THUE_TNCN_MO_TA[loaiThue]}
        />

        <p className="text-phu text-chu-mo">
          Nhật ký chỉ ghi <strong>tên trường đã đổi</strong>, không ghi CCCD, MST hay số tài khoản.
        </p>
      </div>
    </HopThoai>
  );
}
